<template>
  <ListComponent title="Emergency Trip" :totalCount="totalItems">
    <template #list>
      <v-row class="ma-0" dense align="start">
        <v-col cols="6" sm="4" md="3" lg="2" class="pl-0 pt-0 pb-2">
          <v-text-field
            label="Search"
            placeholder="Trip ID, driver, vehicle, container, created by…"
            outlined
            dense
            append-icon="mdi-magnify"
            clearable
            hide-details="auto"
            v-model="search"
            @click:clear="onSearchClear"
            @keyup.enter="onSearch"
          />
        </v-col>
        <v-col cols="6" sm="4" md="3" lg="2" class="pt-0 pb-2">
          <MultiSelect
            label="Status"
            :items="statusList"
            itemText="text"
            itemValue="value"
            v-model="filters.status"
            hide-details="auto"
            clearable
            @change="onFilterChange"
          />
        </v-col>
        <v-col cols="6" sm="4" md="3" lg="2" class="pt-0 pb-2">
          <MultiSelect
            label="Shift"
            :items="shiftList"
            itemText="text"
            itemValue="value"
            v-model="filters.shift"
            hide-details="auto"
            clearable
            @change="onFilterChange"
          />
        </v-col>
        <v-col cols="6" sm="4" md="3" lg="2" class="pt-0 pb-2">
          <MultiSelect
            label="Legs"
            :items="legsList"
            itemText="text"
            itemValue="value"
            v-model="filters.has_legs"
            hide-details="auto"
            clearable
            @change="onFilterChange"
          />
        </v-col>
        <v-col cols="6" sm="4" md="3" lg="2" class="pt-0 pb-2">
          <MultiSelect
            label="Trailer"
            :items="trailerList"
            itemText="text"
            itemValue="value"
            v-model="filters.has_trailer"
            hide-details="auto"
            clearable
            @change="onFilterChange"
          />
        </v-col>
        <v-col cols="6" sm="4" md="3" lg="2" class="pt-0 pb-2">
          <MultiSelect
            label="Container Mapped"
            :items="containerList"
            itemText="text"
            itemValue="value"
            v-model="filters.has_container"
            hide-details="auto"
            clearable
            @change="onFilterChange"
          />
        </v-col>
        <v-col cols="6" sm="4" md="3" lg="2" class="pt-0 pb-2">
          <Autocomplete
            label="Route"
            :loading="routeLoading"
            :totalItems="routeCount"
            :items="routeList"
            itemText="title"
            itemValue="id"
            @updateList="getRouteList"
            v-model="selectedRoute"
          />
        </v-col>
        <v-col cols="6" sm="4" md="3" lg="2" class="pt-0 pb-2">
          <DateField
            v-model="filters.trip_date_from"
            :inputProps="{
              dense: true,
              outlined: true,
              label: 'Trip Date From',
              'hide-details': 'auto',
              clearable: true,
            }"
            @change="onFilterChange"
          />
        </v-col>
        <v-col cols="6" sm="4" md="3" lg="2" class="pt-0 pb-2">
          <DateField
            v-model="filters.trip_date_to"
            :inputProps="{
              dense: true,
              outlined: true,
              label: 'Trip Date To',
              'hide-details': 'auto',
              clearable: true,
            }"
            @change="onFilterChange"
          />
        </v-col>
        <v-col cols="6" sm="4" md="3" lg="2" class="pt-0 pb-2">
          <DateField
            v-model="filters.created"
            :inputProps="{
              dense: true,
              outlined: true,
              label: 'Created Date',
              'hide-details': 'auto',
              clearable: true,
            }"
            @change="onFilterChange"
          />
        </v-col>
        <v-col cols="12" sm="8" md="6" lg="4" class="pt-0 pb-2 d-flex align-center">
          <v-switch
            :input-value="!!filters.is_non_bonded"
            color="deep-purple darken-1"
            label="Non Bonded"
            hide-details
            inset
            dense
            class="ma-0 pa-0 flex-grow-0"
            @change="toggleNonBondedFilter"
          />
          <v-btn
            text
            small
            color="deep-purple darken-1"
            class="ml-4"
            @click="openNonBondedContainers"
          >
            View Non Bonded Containers
            <v-icon right small>mdi-arrow-right</v-icon>
          </v-btn>
        </v-col>

        <v-col cols="12" class="pa-0">
          <AgGridVue
            style="width: 100%; height: calc(100vh - 190px)"
            class="ag-theme-alpine"
            :columnDefs="headers"
            :context="context"
            @grid-ready="onGridReady"
            :defaultColDef="defaultColDef"
            :grid-options="gridOptions"
            :rowData="allList"
          />
        </v-col>
        <v-col v-if="totalItems > 0" cols="12" class="d-flex justify-end">
          <Pagination
            :pageNo="pageNo"
            :totalItems="totalItems"
            :pageSize="itemsPerPage"
            @itemsPerPageChange="itemsPerPageChanged"
            @prevPage="prevPage"
            @nextPage="nextPage"
          />
        </v-col>
      </v-row>
    </template>
    <template #dialogs>
      <BulkUpload
        v-model="bulkUploadDialog"
        :uploadTo="uploadType"
        @refreshList="getList()"
      />
      <NonBondedBulkUpload
        v-model="nonBondedBulkUploadDialog"
        @refreshList="getList()"
      />
      <UpdateTrailerDialog
        v-model="showUpdateTrailerDialog"
        :trip="selectedTrip"
        @updated="getList()"
      />
      <UpdateContainerDialog
        v-model="showUpdateContainerDialog"
        :trip="selectedTrip"
        @updated="getList()"
      />
      <DeleteTripDialog
        v-model="showDeleteTripDialog"
        :trip="selectedTrip"
        @deleted="getList()"
      />
      <TripLogsDialog
        v-model="showTripLogsDialog"
        :tripDetails="selectedTripLogs"
      />
      <v-dialog
        v-model="showStatsDialog"
        max-width="1500"
        scrollable
        transition="dialog-bottom-transition"
      >
        <v-card>
          <v-card-title class="primary white--text py-3 px-5">
            <v-icon left color="white">mdi-chart-timeline-variant</v-icon>
            <span class="text-h6 font-weight-medium">
              Emergency Trips — Daily Fill
            </span>
            <v-spacer />
            <v-btn
              outlined
              small
              dark
              class="mr-2"
              :loading="statsExportLoading"
              :disabled="!statsRows.length"
              @click="exportStats"
            >
              <v-icon left small>mdi-download</v-icon>
              Export
            </v-btn>
            <v-btn icon @click="showStatsDialog = false">
              <v-icon class="white--text">mdi-close</v-icon>
            </v-btn>
          </v-card-title>

          <v-card-text class="pt-5 px-5" style="max-height: 88vh">
            <p class="text-body-2 grey--text text--darken-1 mb-4">
              Grouped by the trip's <strong>created date</strong> (upload day),
              not trip date. <strong>User Trips</strong> = trips created by a
              user (auto-created trips shown separately as
              <strong>Auto-Created (System)</strong>).
              <strong>Container Mapped</strong> = the trip has at least one
              container linked. <strong>Fill %</strong> = Mapped ÷ User Trips.
            </p>

            <!-- Date range + summary tiles -->
            <v-row class="ma-0 mb-4" align="stretch">
              <v-col cols="12" md="4" class="pl-0">
                <v-card outlined class="pa-3 fill-height">
                  <div class="text-caption font-weight-bold grey--text mb-2">
                    CREATED DATE RANGE
                  </div>
                  <div class="d-flex" style="gap: 10px">
                    <DateField
                      v-model="statsFilters.trip_date_from"
                      :inputProps="{
                        dense: true,
                        outlined: true,
                        label: 'From',
                        'hide-details': 'auto',
                        clearable: true,
                      }"
                      @change="getStats"
                    />
                    <DateField
                      v-model="statsFilters.trip_date_to"
                      :inputProps="{
                        dense: true,
                        outlined: true,
                        label: 'To',
                        'hide-details': 'auto',
                        clearable: true,
                      }"
                      @change="getStats"
                    />
                  </div>
                  <div class="text-caption grey--text mt-2">
                    Empty = today only.
                  </div>
                </v-card>
              </v-col>
              <v-col cols="12" md="8" class="pr-0">
                <v-row class="ma-0 fill-height" dense>
                  <v-col cols="6" md="2">
                    <v-card outlined class="pa-3 text-center fill-height">
                      <div
                        class="text-caption font-weight-bold text-uppercase grey--text text--darken-2"
                      >
                        Total Trips
                      </div>
                      <div class="text-h5 font-weight-bold">
                        {{ statsTotalTrips }}
                      </div>
                    </v-card>
                  </v-col>
                  <v-col cols="6" md="2">
                    <v-card outlined class="pa-3 text-center fill-height">
                      <div
                        class="text-caption font-weight-bold text-uppercase grey--text text--darken-2"
                      >
                        User Trips
                      </div>
                      <div class="text-h5 font-weight-bold">
                        {{ statsTotals.user_trips }}
                      </div>
                    </v-card>
                  </v-col>
                  <v-col cols="6" md="2">
                    <v-card
                      outlined
                      class="pa-3 text-center fill-height"
                      style="border-color: #4caf50"
                    >
                      <div
                        class="text-caption font-weight-bold text-uppercase grey--text text--darken-2"
                      >
                        Mapped
                      </div>
                      <div class="text-h5 font-weight-bold success--text">
                        {{ statsTotals.trips_with_container }}
                      </div>
                    </v-card>
                  </v-col>
                  <v-col cols="6" md="2">
                    <v-card
                      outlined
                      class="pa-3 text-center fill-height"
                      style="border-color: #ff7043"
                    >
                      <div
                        class="text-caption font-weight-bold text-uppercase grey--text text--darken-2"
                      >
                        No Container
                      </div>
                      <div
                        class="text-h5 font-weight-bold"
                        style="color: #ff7043"
                      >
                        {{ statsTotals.trips_without_container }}
                      </div>
                    </v-card>
                  </v-col>
                  <v-col cols="6" md="2">
                    <v-card
                      outlined
                      class="pa-3 text-center fill-height"
                      style="border-color: #607d8b"
                    >
                      <div
                        class="text-caption font-weight-bold text-uppercase grey--text text--darken-2"
                      >
                        Auto-Created (System)
                      </div>
                      <div
                        class="text-h5 font-weight-bold"
                        style="color: #607d8b"
                      >
                        {{ statsTotals.system_trips }}
                      </div>
                    </v-card>
                  </v-col>
                  <v-col cols="6" md="2">
                    <v-card outlined class="pa-3 text-center fill-height">
                      <div
                        class="text-caption font-weight-bold text-uppercase grey--text text--darken-2"
                      >
                        Fill Rate
                      </div>
                      <div class="text-h5 font-weight-bold">
                        {{ statsFillRate }}%
                      </div>
                      <v-progress-linear
                        :value="statsFillRate"
                        color="success"
                        height="4"
                        rounded
                        class="mt-1"
                      />
                    </v-card>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>

            <v-alert
              v-if="statsDateRangeInvalid"
              type="warning"
              dense
              text
              class="mb-3 text-caption"
            >
              "From" date is after "To" date — swap them.
            </v-alert>

            <div v-if="statsLoading" class="text-center py-10">
              <v-progress-circular indeterminate color="primary" size="40" />
            </div>

            <template v-else>
              <v-card v-if="statsRows.length" outlined class="pa-4 mb-4">
                <div
                  class="text-subtitle-2 font-weight-bold grey--text text--darken-1 mb-2"
                >
                  Trips per created date
                </div>
                <div style="height: 300px; position: relative">
                  <Chart
                    :labels="statsChartLabels"
                    :datasets="statsChartDatasets"
                    :options="statsChartOptions"
                    :height="300"
                  />
                </div>
              </v-card>
              <div v-else class="text-center py-10 grey--text text-body-2">
                <v-icon size="40" color="grey lighten-1">
                  mdi-database-off-outline
                </v-icon>
                <div class="mt-2">No emergency trips in this range.</div>
              </div>

              <v-card outlined v-if="statsRows.length">
                <v-simple-table dense>
                  <thead>
                    <tr>
                      <th>Created Date</th>
                      <th class="text-right">Total</th>
                      <th class="text-right">User Trips</th>
                      <th class="text-right">Mapped</th>
                      <th class="text-right">No Container</th>
                      <th class="text-right">Fill %</th>
                      <th class="text-right">Auto-Created (System)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="row in statsRows" :key="row.trip_date">
                      <td class="font-weight-medium">{{ row.trip_date }}</td>
                      <td class="text-right font-weight-medium">
                        {{ row.user_trips + row.system_trips }}
                      </td>
                      <td class="text-right">{{ row.user_trips }}</td>
                      <td class="text-right success--text font-weight-medium">
                        {{ row.trips_with_container }}
                      </td>
                      <td
                        class="text-right font-weight-medium"
                        style="color: #ff7043"
                      >
                        {{ row.trips_without_container }}
                      </td>
                      <td class="text-right">
                        {{
                          row.user_trips
                            ? Math.round(
                                (row.trips_with_container / row.user_trips) *
                                  100,
                              )
                            : 0
                        }}%
                      </td>
                      <td class="text-right" style="color: #607d8b">
                        {{ row.system_trips }}
                      </td>
                    </tr>
                  </tbody>
                  <tfoot>
                    <tr class="font-weight-bold grey lighten-4">
                      <td>Total</td>
                      <td class="text-right">{{ statsTotalTrips }}</td>
                      <td class="text-right">{{ statsTotals.user_trips }}</td>
                      <td class="text-right">
                        {{ statsTotals.trips_with_container }}
                      </td>
                      <td class="text-right">
                        {{ statsTotals.trips_without_container }}
                      </td>
                      <td class="text-right">{{ statsFillRate }}%</td>
                      <td class="text-right">
                        {{ statsTotals.system_trips }}
                      </td>
                    </tr>
                  </tfoot>
                </v-simple-table>
              </v-card>
            </template>
          </v-card-text>
        </v-card>
      </v-dialog>

      <v-dialog v-model="exportDialog" max-width="720" persistent>
        <v-card>
          <v-card-title class="primary white--text py-2">
            <span class="text-subtitle-1">Export Emergency Trips</span>
            <v-spacer />
            <v-btn icon @click="exportDialog = false">
              <v-icon class="white--text">mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text class="pt-5">
            <div class="text-caption grey--text mb-3">
              The export uses the filters below. They are pre-filled from the
              list; adjust as needed.
            </div>
            <v-row dense>
              <v-col cols="12" sm="6">
                <v-text-field
                  label="Search"
                  outlined
                  dense
                  clearable
                  hide-details="auto"
                  v-model="exportFilters.search"
                />
              </v-col>
              <v-col cols="6" sm="3">
                <MultiSelect
                  label="Status"
                  :items="statusList"
                  itemText="text"
                  itemValue="value"
                  v-model="exportFilters.status"
                  hide-details="auto"
                  clearable
                />
              </v-col>
              <v-col cols="6" sm="3">
                <MultiSelect
                  label="Shift"
                  :items="shiftList"
                  itemText="text"
                  itemValue="value"
                  v-model="exportFilters.shift"
                  hide-details="auto"
                  clearable
                />
              </v-col>
              <v-col cols="6" sm="3">
                <MultiSelect
                  label="Legs"
                  :items="legsList"
                  itemText="text"
                  itemValue="value"
                  v-model="exportFilters.has_legs"
                  hide-details="auto"
                  clearable
                />
              </v-col>
              <v-col cols="6" sm="3">
                <MultiSelect
                  label="Trailer"
                  :items="trailerList"
                  itemText="text"
                  itemValue="value"
                  v-model="exportFilters.has_trailer"
                  hide-details="auto"
                  clearable
                />
              </v-col>
              <v-col cols="6" sm="3">
                <MultiSelect
                  label="Container Mapped"
                  :items="containerList"
                  itemText="text"
                  itemValue="value"
                  v-model="exportFilters.has_container"
                  hide-details="auto"
                  clearable
                />
              </v-col>
              <v-col cols="6" sm="3">
                <Autocomplete
                  label="Route"
                  :loading="routeLoading"
                  :totalItems="routeCount"
                  :items="routeList"
                  itemText="title"
                  itemValue="id"
                  @updateList="getRouteList"
                  v-model="exportRouteObj"
                />
              </v-col>
              <v-col cols="6" sm="4">
                <DateField
                  v-model="exportFilters.trip_date_from"
                  :inputProps="{
                    dense: true,
                    outlined: true,
                    label: 'Trip Date From',
                    'hide-details': 'auto',
                    clearable: true,
                  }"
                />
              </v-col>
              <v-col cols="6" sm="4">
                <DateField
                  v-model="exportFilters.trip_date_to"
                  :inputProps="{
                    dense: true,
                    outlined: true,
                    label: 'Trip Date To',
                    'hide-details': 'auto',
                    clearable: true,
                  }"
                />
              </v-col>
              <v-col cols="6" sm="4">
                <DateField
                  v-model="exportFilters.created"
                  :inputProps="{
                    dense: true,
                    outlined: true,
                    label: 'Created Date',
                    'hide-details': 'auto',
                    clearable: true,
                  }"
                />
              </v-col>
            </v-row>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn text @click="exportDialog = false">Cancel</v-btn>
            <v-btn
              color="primary"
              depressed
              :loading="exportLoading"
              @click="exportToExcel"
            >
              <v-icon left>mdi-download</v-icon>
              <span>Export</span>
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </template>
    <template #actions>
      <v-tooltip bottom>
        <template #activator="{ on, attrs }">
          <v-btn
            icon
            color="primary"
            depressed
            class="mr-2"
            v-bind="attrs"
            v-on="on"
            @click="getList()"
          >
            <v-icon>mdi-refresh</v-icon>
          </v-btn>
        </template>
        <span>Refresh</span>
      </v-tooltip>
      <v-btn
        color="primary"
        depressed
        outlined
        class="mr-3"
        @click="openStatsDialog"
      >
        <v-icon left>mdi-chart-bar</v-icon>
        <span>Stats</span>
      </v-btn>
      <v-btn
        color="primary"
        depressed
        outlined
        class="mr-3"
        @click="openExportDialog"
      >
        <v-icon left>mdi-download</v-icon>
        <span>Export</span>
      </v-btn>
      <v-menu v-if="permissions && permissions.add" offset-y>
        <template #activator="{ on, attrs }">
          <v-btn
            class="ml-3"
            color="primary"
            depressed
            v-bind="attrs"
            v-on="on"
          >
            <v-icon>mdi-upload</v-icon>
            <span>Bulk Upload</span>
            <v-icon right>mdi-chevron-down</v-icon>
          </v-btn>
        </template>
        <v-list dense>
          <v-list-item @click="bulkUploadDialog = true">
            <v-list-item-title>Bulk Upload</v-list-item-title>
          </v-list-item>
          <v-list-item @click="nonBondedBulkUploadDialog = true">
            <v-list-item-title>Non Bonded Bulk Upload</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </template>
  </ListComponent>
</template>
<script>
import ListComponent from "@/components/General/ListComponent.vue";
import { AgGridVue } from "ag-grid-vue";
import CustomHeader from "@/components/AgGridComponents/General/CustomHeader.vue";
import BulkUpload from "@/components/General/BulkUpload.vue";
import NonBondedBulkUpload from "@/components/General/NonBondedBulkUpload.vue";
import Pagination from "@/components/General/Pagination.vue";
import MultiSelect from "@/components/FormBaseComponents/MultiSelect.vue";
import Autocomplete from "@/components/FormBaseComponents/Autocomplete.vue";
import DateField from "@/components/FormBaseComponents/DateField.vue";
import DraftTripActionsButton from "@/components/AgGridComponents/DraftTripActionsButton.vue";
import DraftTripStatusChip from "@/components/AgGridComponents/DraftTripStatusChip.vue";
import NonBondedFlagChip from "@/components/AgGridComponents/NonBondedFlagChip.vue";
import UpdateTrailerDialog from "@/components/ModuleBased/dialogForms/UpdateTrailerDialog.vue";
import UpdateContainerDialog from "@/components/ModuleBased/dialogForms/UpdateContainerDialog.vue";
import DeleteTripDialog from "@/components/ModuleBased/dialogForms/DeleteTripDialog.vue";
import TripLogsDialog from "@/components/ModuleBased/dialogForms/TripLogsDialog.vue";
import Chart from "@/components/General/Chart.vue";
import {
  isAuthenticated,
  hasRouteAccess,
  getPermissions,
} from "@/utils/functions";

export default {
  components: {
    ListComponent,
    agColumnHeader: CustomHeader,
    AgGridVue,
    BulkUpload,
    NonBondedBulkUpload,
    Pagination,
    MultiSelect,
    Autocomplete,
    DateField,
    DraftTripActionsButton,
    DraftTripStatusChip,
    NonBondedFlagChip,
    UpdateTrailerDialog,
    UpdateContainerDialog,
    DeleteTripDialog,
    TripLogsDialog,
    Chart,
  },
  data() {
    return {
      allList: [],
      totalItems: 0,
      search: "",
      filters: {
        status: null,
        shift: null,
        route: null,
        has_legs: null,
        has_trailer: null,
        has_container: null,
        is_non_bonded: null,
        trip_date_from: null,
        trip_date_to: null,
        created: null,
      },
      statusList: [
        { text: "Assigned", value: "Assigned" },
        { text: "Active", value: "Active" },
        { text: "Completed", value: "Completed" },
      ],
      shiftList: [
        { text: "Day", value: "Day" },
        { text: "Night", value: "Night" },
      ],
      trailerList: [
        { text: "No Trailer", value: false },
        { text: "Has Trailer", value: true },
      ],
      containerList: [
        { text: "Not Mapped", value: false },
        { text: "Mapped", value: true },
      ],
      legsList: [
        { text: "No Legs", value: false },
        { text: "Has Legs", value: true },
      ],
      routeList: [],
      routeCount: 0,
      routeLoading: false,
      selectedRouteObj: null,
      pageNo: 1,
      itemsPerPage: 50,
      exportLoading: false,
      exportDialog: false,
      exportRouteObj: null,
      exportFilters: {
        search: null,
        status: null,
        shift: null,
        route: null,
        has_legs: null,
        has_trailer: null,
        has_container: null,
        trip_date_from: null,
        trip_date_to: null,
        created: null,
      },
      bulkUploadDialog: false,
      nonBondedBulkUploadDialog: false,
      uploadType: "trip",
      showUpdateTrailerDialog: false,
      showUpdateContainerDialog: false,
      showDeleteTripDialog: false,
      showTripLogsDialog: false,
      selectedTrip: null,
      selectedTripLogs: null,
      permissions: null,
      // emergency-trip fill stats (user trips vs auto-created trips)
      statsLoading: false,
      statsExportLoading: false,
      statsRows: [],
      statsTotals: {
        user_trips: 0,
        trips_with_container: 0,
        trips_without_container: 0,
        system_trips: 0,
      },
      statsFilters: {
        trip_date_from: null,
        trip_date_to: null,
      },
      showStatsDialog: false,
      defaultColDef: { resizable: true },
      gridOptions: {
        onGridSizeChanged: () => this.gridOptions.api.sizeColumnsToFit(),
        suppressRowClickSelection: true,
        enableCellTextSelection: true,
        rowHeight: 35,
      },
      headers: [
        {
          headerName: "Trip ID",
          field: "trip_id",
          sortable: true,
          minWidth: 120,
          cellClass: ["primary--text font-weight-bold cursor-pointer"],
          onCellClicked: (params) => this.openTripDetails(params.data),
        },
        {
          headerName: "Status",
          field: "status",
          sortable: true,
          minWidth: 120,
          cellRendererFramework: "DraftTripStatusChip",
        },
        {
          headerName: "Trip Type",
          field: "trip_type",
          sortable: true,
          minWidth: 130,
        },
        {
          headerName: "Flag",
          field: "is_non_bonded",
          sortable: true,
          minWidth: 150,
          cellRendererFramework: "NonBondedFlagChip",
        },
        {
          headerName: "Driver",
          field: "driver_name",
          sortable: true,
          minWidth: 180,
        },
        {
          headerName: "License No.",
          field: "driver_license_number",
          sortable: true,
          minWidth: 150,
        },
        {
          headerName: "Raw Driver (Bulk Upload)",
          field: "raw_driver",
          sortable: true,
          minWidth: 170,
          valueFormatter: (params) => params.value || "-",
        },
        {
          headerName: "Vehicle",
          field: "vehicle_number",
          sortable: true,
          minWidth: 150,
        },
        {
          headerName: "Container No.",
          field: "container_nos",
          sortable: false,
          minWidth: 180,
          valueFormatter: (params) => params.value || "-",
        },
        {
          headerName: "Trailer Mode",
          field: "trailer_mode",
          sortable: true,
          minWidth: 130,
        },
        {
          headerName: "Trailer 1",
          field: "trailer_1_number",
          sortable: true,
          minWidth: 130,
        },
        {
          headerName: "Trailer 2",
          field: "trailer_2_number",
          sortable: true,
          minWidth: 130,
        },
        {
          headerName: "Has Geofence",
          field: "has_geofence",
          sortable: true,
          minWidth: 120,
          valueFormatter: (params) => (params.value ? "Yes" : "No"),
        },
        { headerName: "Shift", field: "shift", sortable: true, minWidth: 100 },
        { headerName: "Route", field: "route", sortable: true, minWidth: 120 },
        {
          headerName: "Trip Date",
          field: "trip_date",
          sortable: true,
          minWidth: 140,
          valueFormatter: (params) =>
            params.value ? this.$globals.formatDate(params.value) : "-",
        },
        {
          headerName: "Created",
          field: "created",
          sortable: true,
          minWidth: 160,
        },
        {
          headerName: "Created By",
          field: "created_by",
          sortable: true,
          minWidth: 120,
        },
        {
          headerName: "Actions",
          field: "actions",
          cellRendererFramework: "DraftTripActionsButton",
          pinned: "right",
          minWidth: 210,
        },
      ],
    };
  },
  computed: {
    context() {
      return { parentComponent: this };
    },
    offset() {
      return this.itemsPerPage * (this.pageNo - 1);
    },
    selectedRoute: {
      get() {
        return this.selectedRouteObj;
      },
      set(value) {
        this.selectedRouteObj = value;
        this.filters.route = value ? value.id : null;
        this.onFilterChange();
      },
    },
    statsTotalTrips() {
      return (
        (this.statsTotals.user_trips || 0) +
        (this.statsTotals.system_trips || 0)
      );
    },
    statsFillRate() {
      const t = this.statsTotals.user_trips || 0;
      if (!t) return 0;
      return Math.round(
        ((this.statsTotals.trips_with_container || 0) / t) * 100,
      );
    },
    statsDateRangeInvalid() {
      const f = this.statsFilters.trip_date_from;
      const t = this.statsFilters.trip_date_to;
      return !!(f && t && f > t);
    },
    statsChartLabels() {
      return this.statsRows.map((r) => r.trip_date);
    },
    statsChartDatasets() {
      return [
        {
          label: "Container mapped",
          backgroundColor: "#4CAF50",
          data: this.statsRows.map((r) => r.trips_with_container),
        },
        {
          label: "No container",
          backgroundColor: "#FF7043",
          data: this.statsRows.map((r) => r.trips_without_container),
        },
        {
          label: "Auto-Created (System)",
          backgroundColor: "#607D8B",
          data: this.statsRows.map((r) => r.system_trips),
        },
      ];
    },
    statsChartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          xAxes: [
            {
              stacked: true,
              // keep bars a sensible width even with only 1-2 days
              barPercentage: 0.5,
              categoryPercentage: 0.5,
              maxBarThickness: 90,
            },
          ],
          yAxes: [
            { stacked: true, ticks: { beginAtZero: true, precision: 0 } },
          ],
        },
        legend: { position: "bottom" },
        tooltips: { mode: "index", intersect: false },
      };
    },
  },
  watch: {
    showStatsDialog(open) {
      if (!open) {
        // clear the dialog's own date range for next open
        this.statsFilters.trip_date_from = null;
        this.statsFilters.trip_date_to = null;
      }
    },
  },
  methods: {
    openTripDetails(row) {
      if (row && row.id) {
        this.$router.push({
          path: `/app/admin/draft-trip/details/${row.id}`,
          query: {},
        });
      }
    },
    openUpdateTrailer(row) {
      this.selectedTrip = row;
      this.showUpdateTrailerDialog = true;
    },
    openUpdateContainer(row) {
      this.selectedTrip = row;
      this.showUpdateContainerDialog = true;
    },
    openDeleteTrip(row) {
      this.selectedTrip = row;
      this.showDeleteTripDialog = true;
    },
    openTripLogs(row) {
      if (!row || !row.id) return;
      this.$api.draftTrip.getDraftTripObject(row.id).then((res) => {
        this.selectedTripLogs = res.data || res;
        this.showTripLogsDialog = true;
      });
    },
    onSearch() {
      this.pageNo = 1;
      this.getList();
    },
    onSearchClear() {
      this.search = "";
      this.pageNo = 1;
      this.getList();
    },
    onFilterChange() {
      this.pageNo = 1;
      this.getList();
    },
    toggleNonBondedFilter(value) {
      this.filters.is_non_bonded = value ? true : null;
      this.onFilterChange();
    },
    openNonBondedContainers() {
      this.$router.push("/app/admin/non-bonded-container/list");
    },
    getRouteList(params = {}) {
      this.routeLoading = true;
      params = {
        is_active: true,
        limited: true,
        offset: 0,
        limit: 10,
        ...params,
      };
      this.$api.lane
        .getLaneList(params)
        .then((res) => {
          this.routeLoading = false;
          if ("count" in res) {
            this.routeCount = res.count;
          }
          if (params.offset == 0) {
            this.routeList = res.data;
          } else {
            this.routeList.push(...res.data);
          }
        })
        .catch(() => {
          this.routeLoading = false;
        });
    },
    itemsPerPageChanged(e) {
      this.pageNo = 1;
      this.itemsPerPage = e;
      this.getList();
    },
    prevPage() {
      this.pageNo--;
      this.getList();
    },
    nextPage() {
      this.pageNo++;
      this.getList();
    },
    onGridReady(params) {
      this.gridApi = params.api;
      this.gridColumnApi = params.columnApi;
    },
    buildFilterParams(src) {
      // src: { search, ...filters }. Defaults to the page's own filters.
      const f = src || { ...this.filters, search: this.search };
      const params = {};
      if (f.search) params.search = f.search;
      if (f.status) params.status = f.status;
      if (f.shift) params.shift = f.shift;
      if (f.route) params.route = f.route;
      if (f.has_legs !== null && f.has_legs !== undefined)
        params.has_legs = f.has_legs;
      if (f.has_trailer !== null && f.has_trailer !== undefined)
        params.has_trailer = f.has_trailer;
      if (f.has_container !== null && f.has_container !== undefined)
        params.has_container = f.has_container;
      if (f.is_non_bonded !== null && f.is_non_bonded !== undefined)
        params.is_non_bonded = f.is_non_bonded;
      if (f.trip_date_from) params.trip_date_from = f.trip_date_from;
      if (f.trip_date_to) params.trip_date_to = f.trip_date_to;
      if (f.created) params.created = f.created;
      return params;
    },
    getList() {
      this.$bus.$emit("showLoader", true);
      const params = {
        offset: this.offset,
        limit: this.itemsPerPage,
        ...this.buildFilterParams(),
      };
      this.$api.draftTrip
        .getDraftTripList(params)
        .then((res) => {
          this.allList = res.data;
          this.totalItems = res.count != null ? res.count : res.data.length;
          this.$bus.$emit("showLoader", false);
        })
        .catch((err) => {
          console.error(err);
          this.$bus.$emit("showLoader", false);
        });
    },
    getStats() {
      if (this.statsDateRangeInvalid) {
        // "From" after "To" - don't call, the warning alert is shown.
        this.statsRows = [];
        this.statsTotals = {
          user_trips: 0,
          trips_with_container: 0,
          trips_without_container: 0,
          system_trips: 0,
        };
        return;
      }
      this.statsLoading = true;
      const params = {};
      if (this.filters.route) params.route = this.filters.route;
      if (this.filters.status) params.status = this.filters.status;
      if (this.filters.shift) params.shift = this.filters.shift;
      // dialog's own date range takes precedence; fall back to the page's
      // Trip Date From/To filters.
      const from =
        this.statsFilters.trip_date_from || this.filters.trip_date_from;
      const to = this.statsFilters.trip_date_to || this.filters.trip_date_to;
      if (from) params.trip_date_from = from;
      if (to) params.trip_date_to = to;
      this.$api.draftTrip
        .getDraftTripStats(params)
        .then((res) => {
          this.statsRows = res.results || [];
          this.statsTotals = res.totals || {
            user_trips: 0,
            trips_with_container: 0,
            trips_without_container: 0,
            system_trips: 0,
          };
        })
        .catch((err) => console.error(err))
        .finally(() => {
          this.statsLoading = false;
        });
    },
    openStatsDialog() {
      // seed the dialog's date range from the page's Trip Date From/To
      // filter so it matches what the list is showing.
      this.statsFilters.trip_date_from = this.filters.trip_date_from || null;
      this.statsFilters.trip_date_to = this.filters.trip_date_to || null;
      this.showStatsDialog = true;
      this.getStats();
    },
    statsParams() {
      const params = {};
      if (this.filters.route) params.route = this.filters.route;
      if (this.filters.status) params.status = this.filters.status;
      if (this.filters.shift) params.shift = this.filters.shift;
      const from =
        this.statsFilters.trip_date_from || this.filters.trip_date_from;
      const to = this.statsFilters.trip_date_to || this.filters.trip_date_to;
      if (from) params.trip_date_from = from;
      if (to) params.trip_date_to = to;
      return params;
    },
    exportStats() {
      this.statsExportLoading = true;
      this.$api.draftTrip
        .exportDraftTripStats(this.statsParams())
        .then((res) => {
          const url = window.URL.createObjectURL(new Blob([res.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute(
            "download",
            `EmergencyTrips_DailyFill_${Date.now()}.xlsx`
          );
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
        })
        .catch((err) => {
          console.error(err);
          this.$bus.$emit("showToastMessage", {
            message: "Failed to export stats",
            color: "error",
          });
        })
        .finally(() => {
          this.statsExportLoading = false;
        });
    },
    openExportDialog() {
      // pre-fill the dialog filters from whatever is applied on the list
      this.exportFilters = {
        search: this.search || null,
        status: this.filters.status,
        shift: this.filters.shift,
        route: this.filters.route,
        has_legs: this.filters.has_legs,
        has_trailer: this.filters.has_trailer,
        has_container: this.filters.has_container,
        trip_date_from: this.filters.trip_date_from,
        trip_date_to: this.filters.trip_date_to,
        created: this.filters.created,
      };
      this.exportRouteObj = this.selectedRouteObj;
      this.exportDialog = true;
    },
    exportToExcel() {
      this.exportLoading = true;
      const src = {
        ...this.exportFilters,
        route: this.exportRouteObj ? this.exportRouteObj.id : null,
      };
      this.$api.draftTrip
        .exportToExcel(this.buildFilterParams(src))
        .then((res) => {
          const url = window.URL.createObjectURL(new Blob([res.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", `EmergencyTrips_${Date.now()}.xlsx`);
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
          this.exportDialog = false;
        })
        .catch((err) => {
          console.error(err);
          this.$bus.$emit("showToastMessage", {
            message: "Failed to export",
            color: "error",
          });
        })
        .finally(() => (this.exportLoading = false));
    },
  },
  beforeMount() {
    if (!isAuthenticated()) {
      this.$router.push("/");
    } else if (!hasRouteAccess(this.$route)) {
      this.$router.push(`/${localStorage.getItem("user_type").toLowerCase()}`);
    } else {
      const permissions = getPermissions("draft_trip");
      if (!permissions || !permissions.view) {
        this.$bus.$emit("showToastMessage", {
          message: "You don't have access to this module!",
          color: "error",
        });
        this.$router.push(
          `/${localStorage.getItem("user_type").toLowerCase()}`,
        );
      } else {
        this.permissions = { ...permissions };
        this.getRouteList();
        this.getList();
      }
    }
  },
};
</script>
